import dayjs from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import {
  DataModelBuilder, SerializationContext, SerializedData,
} from '../../dto/data-model.js';
import { cloneListOfCloneable, isSameData, isSameList } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { SeatInfo } from './seat-info.js';
import { TimeZone } from '../../../time/timezone.js';
import { TrueTime } from '../../../time/true-time.js';
import { SeatInfoSerializer } from '../../../serialization/seat-info-serializer.js';
import { SeatingStateBase } from './seating-state-base.js';

/**
 * Use {@link SeatingStateHelper} to mutate instances of this class.
 */
export class EventSeatingState extends SeatingStateBase {
  static readonly TYPE_ID: string = '27e7b5e64e3c40ce8f45e0025339bae3';

  constructor(
    public readonly seats: SeatInfo[],
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: dayjs.Dayjs,
    public readonly eventId: string,
  ) {
    super();
  }

  get $typeId(): string {
    return EventSeatingState.TYPE_ID;
  }

  static empty({
    businessRef,
    eventId,
  }: {
    businessRef: BusinessModuleRef,
    eventId: string
  }): EventSeatingState {
    return new EventSeatingState(
        [],
        businessRef,
        TrueTime.now(),
        eventId,
    );
  }

  static fromMap(
      context: SerializationContext,
      map: SerializedData,
  ): EventSeatingState {
    return new EventSeatingState(
        SeatInfoSerializer.deserialize(context.fromBinaryBlob(MapUtils.getRaw(map, 'seats'))),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<string>(map, 'eventId'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      seatCount: this.seatCount,
      seats: context.toBinaryBlob(SeatInfoSerializer.serialize(this.seats)),
      businessRef: this.businessRef.encode(),
      creationDate: context.encodeDate(this.creationDate),
      eventId: this.eventId,
    };
  }

  clone(): this {
    return new EventSeatingState(
        cloneListOfCloneable(this.seats),
        this.businessRef.clone(),
        TimeZone.cloneDate(this.creationDate),
        this.eventId,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.seatCount === this.seatCount &&
        isSameList(other.seats, this.seats, (a, b) => isSameData(a, b)) &&
        isSameData(other.businessRef, this.businessRef) &&
        other.creationDate === this.creationDate &&
        other.eventId === this.eventId;
  }
}

export class EventSeatingStateBuilder extends DataModelBuilder<EventSeatingState> {
  build(context: SerializationContext, map: SerializedData): EventSeatingState {
    return EventSeatingState.fromMap(context, map);
  }
}
